import React, { Fragment, useEffect, useState } from "react";
import { EVENTS } from "@upsolve/shared";
import { A, ACSS, IconBook, H1, H5, HR, P, Small } from "@upsolve/ui";
import styled from "styled-components";
import _ from "lodash";
import Link from "gatsby-link";

import * as CONTENT_CREATION_METHODS from "../components/analytics/contentCreationMethods";
import Article from "../components/Layout/Article.div";
import AuthorBio from "../components/Layout/AuthorBio";
import AuthorsBlock from "../components/Layout/AuthorsBlock";
import CTASideBySideOfferings from "../components/CTA/CTASideBySideOfferings";
import CitationsList from "../components/Layout/CitationsList";
import ContentSectionFooter from "../components/Layout/ContentSectionFooter";
import DisabledReadingPrompt from "../components/CTA/DisabledReadingPrompt";
import HeroFilerCta from "../components/CTA/HeroFilerCta";
import MainFooter from "../components/Layout/Footer";
import MainHeader from "../components/Layout/Header";
import RelatedArticles from "../components/Programmatic/RelatedArticles";
import SEO from "../components/SEO/SEO";
import TableOfContents from "../components/Layout/TableOfContents";
import TopLevelLayout from "../components/Layout/TopLevelLayout";
import UpsolveTextBio from "../components/ProofBuilders/UpsolveTextBio";
import VideoInContext from "../components/Media/VideoInContext";
import checkForInlineCta from "../components/Contentful/checkForInlineCta";
import getCitationsFromRichText from "../components/Contentful/getCitationsFromRichText";
import getInlineCTAComponents from "../components/CTA/getInlineCTAComponents";
import getWordCountFromRichText from "../components/Contentful/getWordCountFromRichText";
import richTextNodeToComponent from "../components/Contentful/richTextNodeToComponent";
import richTextNodesInSections from "../components/Contentful/richTextNodesInSections";
import { track } from "../components/analytics/track";
import withGeoLocation from "../components/maps/withGeoLocation";
import withScrollWatcher from "../components/analytics/withScrollWatcher";
import { Ampify } from "../components/AMP/AmpContext";

const templateProps = {
  pageComponentName: "CommunityQA",
  pageComponentVersion: 0,
};
const CTAS = getInlineCTAComponents();
const CTA = CTAS.CTAUpsolveCommunity;
/**
 * CommunityQA
 * - v0: Where we began.
 *
 * @component
 * @version 0
 */
const CommunityQA = (props) => {
  const { pageContext } = props;
  const { amp, slug, title, relatedArticles } = pageContext;
  const cta = `File Bankruptcy for free`;
  const trackingPageContext = {
    contentCreationMethod: CONTENT_CREATION_METHODS.USER_GENERATED,
    slug,
    title,
    ...templateProps,
  };

  // On Mount
  useEffect(() => {
    track(EVENTS.PAGE_VIEW, { pageContext: trackingPageContext });
  }, []);

  // Comp
  const children = (
    <TopLevelLayout showPersistentCta={false} persistentCtaText={cta} trackingPageContext={trackingPageContext}>
      <SEO title={title} description={`${title} from the Upsolve community.`} />
      <MainHeader trackingPageContext={trackingPageContext} />
      <Article>
        <nav className="article__breadcrumb" aria-label="Breadcrumb" data-test-id="breadcrumb">
          <ol>
            <li>
              <a aria-busy="false" href="/learn/">
                Learning Center
              </a>
            </li>
          </ol>
        </nav>
        <div className="article__body">
          <div className="article__content">
            <div className="content-hero">
              <header className="content-hero__header">
                <H1 as="h1">{title}</H1>
                <UpsolveTextBio />
                <HR />
              </header>
            </div>
            <div className="content-body">
              <article className="content-body__container">
                <AuthorsBlock verb="Compiled" />
                <CTA
                  qaVariant
                  key="1"
                  dataTestId="inline-content-cta"
                  postSearchResults={pageContext.postSearchResults}
                  trackingPageContext={trackingPageContext}
                />
                <ContentSectionFooter />
                <HR />
              </article>
              <aside className="article__sidebar">
                <div className="sidebar-hero__cta">
                  <HeroFilerCta trackingPageContext={trackingPageContext} />
                </div>
              </aside>
            </div>
          </div>
        </div>
      </Article>
      <StyledRelatedArticles>
        <H5 as="p">Continue reading and learning!</H5>
        <div>
          {relatedArticles.map((keyword) => (
            <Link key={keyword} to={`/discussion/${_.kebabCase(keyword)}`}>
              <div className="ra__cover">
                <IconBook />
              </div>
              <div className="ra__title">"{_.startCase(keyword)}" Discussion</div>
            </Link>
          ))}
        </div>
      </StyledRelatedArticles>
      <CTASideBySideOfferings trackingPageContext={trackingPageContext} />
      <MainFooter showArticles />
    </TopLevelLayout>
  );

  return amp === true ? <Ampify>{children}</Ampify> : children;
};

const StyledRelatedArticles = styled.div`
  max-width: 1080px;
  margin: 0 auto 2em;
  & > p {
    width: 100%;
    text-align: center;
    color: ${(props) => props.theme.colors.gray[900]};
    opacity: 0.9;
  }
  & > div {
    width: 100%;
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    grid-template-rows: auto;
    & > a {
      height: auto;
      border-radius: 4px;
      margin: 1em 0.5em;
      overflow: hidden;
      background: white;
      box-shadow: 0 4px 16px rgba(0, 0, 25, 0.1);
      display: flex;
      flex-direction: column;
      .ra__cover {
        background: linear-gradient(
          0deg,
          ${(props) => props.theme.colors.white[700]},
          ${(props) => props.theme.colors.white[800]}
        );
        min-height: 32px;
        padding: 1em;
        display: flex;
        justify-content: center;
        align-items: center;
        svg path {
          fill: ${(props) => props.theme.colors.white[100]};
        }
      }
      .ra__title {
        height: auto;
        padding: 1em;
        ${ACSS}
        small {
          opacity: 0.5;
        }
      }
    }
    @media (max-width: ${(props) => props.theme.breakpoints[500]}) {
      grid-template-columns: repeat(1, 1fr);
      & > a {
        margin: 1em 2em;
      }
    }
  }
`;

export default CommunityQA;
